import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import { UniqueQueryKeys } from '@/types';

interface DeleteQueryParams {
  queryId: string;
}

/**
 * Hook to delete a saved custom query from the backend.
 *
 * Sends DELETE request to /my endpoint with setting name format:
 * "saved_query#<query-id>"
 *
 * On success:
 * - Refetches 'my' 'setting' cache to trigger immediate update
 * - Shows success toast
 * - Automatically updates Custom Queries folder via useSavedQueries refetch
 *
 * On error:
 * - Shows error toast with message
 * - No optimistic updates (wait for server confirmation)
 *
 * Architecture notes:
 * - Follows existing useSaveQuery.ts pattern for cache invalidation
 * - Uses UniqueQueryKeys.MY constant for consistency
 * - refetchQueries with type: 'active' ensures only active queries refetch
 */
export function useDeleteQuery() {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { dispatch, setMode, queryState } = useQueryBuilder();

  return useMutation({
    mutationFn: async ({ queryId }: DeleteQueryParams) => {
      const settingName = `saved_query#${queryId}`;

      const response = await axios.delete('/setting', {
        data: {
          name: settingName,
        },
      });

      return response.data;
    },
    onSuccess: async (_data, variables) => {
      // Refetch queries immediately to update Custom Queries folder
      // Matches pattern from useSaveQuery.ts for consistency
      await queryClient.refetchQueries({
        queryKey: [UniqueQueryKeys.MY, 'setting'],
        type: 'active', // Only refetch queries that have active observers
      });

      toast.success('Query deleted successfully');

      const selectedQueryId = queryState?.metadata.id ?? null;
      if (selectedQueryId && selectedQueryId === variables.queryId) {
        setMode(null);
        dispatch({ type: 'CLEAR_QUERY' });
      }
    },
    onError: (error: unknown) => {
      console.error('Failed to delete saved query:', error);
      // Handle both network errors and API error responses
      const axiosError = error as AxiosError<{ message?: string }>;
      const errorMessage =
        axiosError.response?.data?.message ||
        (error instanceof Error ? error.message : undefined) ||
        'Failed to delete query';
      toast.error(errorMessage);
    },
  });
}
